import { web3, walletConnectModalInit, coinbaseWalletInit } from "../web3";
import { Contract } from "web3-eth-contract";
import { AbiItem } from "web3-utils";

// Define interfaces for the return types
interface Web3Data {
  isLoggedIn: boolean;
  accounts: string[];
  error?: boolean;
  code?: number;
  msg?: string;
}

// Define proper types for ethereum methods
type EthereumRequestParams = { method: string; params?: unknown[] };
type EthereumResponse = string | string[] | undefined;

// Define the window ethereum property with proper typing
declare global {
  interface Window {
    ethereum?: {
      request: (args: EthereumRequestParams) => Promise<EthereumResponse>;
      send: (method: string, params?: unknown[]) => Promise<EthereumResponse>;
      isCoinbaseWallet?: boolean;
      isMetaMask?: boolean;
    };
  }
}

/**
 * Get the current network ID from ethereum provider
 * @returns Promise<string> - The network ID
 */
async function getNetworkId(): Promise<string> {
  try {
    return window.ethereum?.request({
      method: "eth_chainId",
    }) as Promise<string>;
  } catch (error) {
    return "1"; // Default to Ethereum mainnet
  }
}

/**
 * Get the web3 instance and check if user is logged in
 * @param isAuthenticate Whether to check authentication
 * @returns Promise<Web3Data> - Object containing login status and accounts
 */
async function getWeb3(isAuthenticate?: boolean): Promise<Web3Data> {
  const web3Data: Web3Data = {
    isLoggedIn: false,
    accounts: [],
  };

  try {
    // Check if we have a web3 instance
    if (web3) {
      const responseData: string[] = await web3.eth.getAccounts();

      if (responseData.length) {
        web3Data.accounts = responseData;
        if (isAuthenticate) {
          web3Data.isLoggedIn = true;
        }
        return web3Data;
      }
    }

    // If no web3 instance or no accounts, try to initialize based on available providers
    if (typeof window.ethereum !== "undefined") {
      // Try to detect which wallet is available
      if (window.ethereum.isCoinbaseWallet) {
        // Initialize Coinbase Wallet
        await coinbaseWalletInit();
        if (web3) {
          const responseData: string[] = await web3.eth.getAccounts();
          if (responseData.length) {
            web3Data.accounts = responseData;
            if (isAuthenticate) {
              web3Data.isLoggedIn = true;
            }
            return web3Data;
          }
        }
      } else if (window.ethereum.isMetaMask) {
        // For MetaMask, try to get accounts directly
        try {
          const responseData = (await window.ethereum.request({
            method: "eth_accounts",
          })) as string[];
          if (responseData && responseData.length) {
            web3Data.accounts = responseData;
            if (isAuthenticate) {
              web3Data.isLoggedIn = true;
            }
            return web3Data;
          }
        } catch (error) {
          console.error("Error getting MetaMask accounts:", error);
        }
      }
    }

    return web3Data;
  } catch (error) {
    console.error("Error in getWeb3:", error);
    return web3Data;
  }
}

/**
 * Enable WalletConnect and get web3 data
 * @returns Promise<Web3Data> - Object containing login status and accounts
 */
async function enabledWalletConnect(): Promise<Web3Data> {
  try {
    await walletConnectModalInit();
    const resp = await getWeb3();
    return resp;
  } catch (error: unknown) {
    if (
      error &&
      typeof error === "object" &&
      "code" in error &&
      error.code === -32002
    ) {
      return {
        isLoggedIn: false,
        accounts: [],
      };
    }
    return {
      isLoggedIn: false,
      accounts: [],
    };
  }
}

/**
 * Enable Metamask connection and get web3 data
 * @returns Promise<Web3Data> - Object containing login status, accounts and possible error info
 */
async function enableMetamask(): Promise<Web3Data> {
  try {
    await window.ethereum?.send("eth_requestAccounts");
    const resp = await getWeb3();
    return resp;
  } catch (error: unknown) {
    if (
      error &&
      typeof error === "object" &&
      "code" in error &&
      "message" in error
    ) {
      const typedError = error as { code: number; message: string };
      if (typedError.code === -32002 || typedError.code === 4001) {
        return {
          error: true,
          code: typedError.code,
          msg: typedError.message,
          isLoggedIn: false,
          accounts: [],
        };
      }
      return {
        error: true,
        code: typedError.code,
        msg: typedError.message,
        isLoggedIn: false,
        accounts: [],
      };
    }
    return {
      error: true,
      code: 0,
      msg: "Unknown error",
      isLoggedIn: false,
      accounts: [],
    };
  }
}

/**
 * Enable Coinbase Wallet connection and get web3 data
 * @returns Promise<Web3Data> - Object containing login status, accounts and possible error info
 */
async function enableCoinbaseWallet(): Promise<Web3Data> {
  try {
    await coinbaseWalletInit();
    await window.ethereum?.request({ method: "eth_requestAccounts" });
    const resp = await getWeb3();
    return resp;
  } catch (error: unknown) {
    if (
      error &&
      typeof error === "object" &&
      "code" in error &&
      "message" in error
    ) {
      const typedError = error as { code: number; message: string };
      if (typedError.code === -32002 || typedError.code === 4001) {
        return {
          error: true,
          code: typedError.code,
          msg: typedError.message,
          isLoggedIn: false,
          accounts: [],
        };
      }
      return {
        error: true,
        code: typedError.code,
        msg: typedError.message,
        isLoggedIn: false,
        accounts: [],
      };
    }
    return {
      error: true,
      code: 0,
      msg: "Unknown error",
      isLoggedIn: false,
      accounts: [],
    };
  }
}

/**
 * Get a contract instance
 * @param contractAbi The ABI of the contract
 * @param contractAddress The address of the contract
 * @returns Promise<Contract | undefined> - Contract instance or undefined if failed
 */
async function getContractInstance(
  contractAbi: AbiItem[],
  contractAddress: string
): Promise<Contract | undefined> {
  try {
    if (web3) {
      const contractInstance = await new web3.eth.Contract(
        contractAbi,
        contractAddress
      );
      return contractInstance;
    }
  } catch (error) {
    // console.log(error);
    return undefined;
  }
}

export const web3Services = {
  getNetworkId,
  enableMetamask,
  enableCoinbaseWallet,
  getContractInstance,
  getWeb3,
  enabledWalletConnect,
};
